import * as missionApi from '@/api/mission'
import { WIDGET_REGISTRY } from '@/widgets/registry'
import {
  normalizeLayout,
  patchActiveWidgets,
  setActiveTab,
  addTab,
  removeTab,
  renameTab,
  parseLayoutImport,
} from '@/widgets/layoutTabs'
import { cueAlertStyle, normalizeGlobal, remainingForPreset, resolveTargetAt, sessionPatchForPreset } from '@/widgets/servicePresets'

let saveTimer = null

function rectsOverlap(a, b) {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
}

function findWidgetPlacement(widgets, colNum, w, h, preferX, preferY) {
  const items = (widgets || []).map((wid) => ({
    x: Number(wid.layout?.x) || 0,
    y: Number(wid.layout?.y) || 0,
    w: Number(wid.layout?.w) || 3,
    h: Number(wid.layout?.h) || 2,
  }))
  const clampX = (x) => Math.max(0, Math.min(x, Math.max(0, colNum - w)))
  const tryPos = (x, y) => {
    const cand = { x: clampX(x), y: Math.max(0, y), w, h }
    return items.every((it) => !rectsOverlap(cand, it)) ? cand : null
  }

  if (preferX != null && preferY != null) {
    const hit = tryPos(preferX, preferY)
    if (hit) return hit
  }

  const startY = preferY != null ? preferY : 0
  for (let y = startY; y < startY + 80; y += 1) {
    for (let x = 0; x <= colNum - w; x += 1) {
      const hit = tryPos(x, y)
      if (hit) return hit
    }
  }

  const maxY = items.reduce((m, it) => Math.max(m, it.y + it.h), 0)
  return { x: 0, y: maxY, w, h }
}

function emptyServiceSession() {
  return {
    serviceId: null,
    running: false,
    anchorMs: null,
    remainingAtStart: 600,
    pausedRemaining: null,
    duration: 600,
    tick: 0,
    alertCuesFired: [],
    targetAt: null,
    mode: 'duration',
  }
}

function pastCountdownCues(remaining, cues) {
  return (cues || []).filter((c) => c.at > remaining).map((c) => c.at)
}

const state = {
  workspaces: [],
  activeWorkspaceId: null,
  activeWorkspaceName: '',
  layout: null,
  editMode: false,
  widgets: [],
  catalog: [],
  presets: {},
  saving: false,
  serviceSession: emptyServiceSession(),
}

const getters = {
  workspaces: (s) => s.workspaces,
  activeWorkspaceId: (s) => s.activeWorkspaceId,
  activeWorkspaceName: (s) => s.activeWorkspaceName,
  layout: (s) => s.layout,
  editMode: (s) => s.editMode,
  layoutTabs: (s) => s.layout?.tabs || [],
  activeTabId: (s) => s.layout?.activeTabId || null,
  activeTab: (s) => (s.layout?.tabs || []).find((t) => t.id === s.layout?.activeTabId) || null,
  gridItems: (s) => (s.layout?.widgets || []).map((w) => ({
    ...w.layout,
    i: w.id,
    widget: w,
  })),
  catalog: (s) => s.catalog,
  presets: (s) => s.presets,
  theme: (s) => (s.layout?.theme === 'dark' ? 'dark' : 'light'),
  servicePresets: (s) => s.layout?.global?.servicePresets || [],
  activeServiceId: (s) => s.layout?.global?.activeServiceId || null,
  activeService: (s) => {
    const presets = s.layout?.global?.servicePresets || []
    const id = s.layout?.global?.activeServiceId
    return presets.find((p) => p.id === id) || presets[0] || null
  },
  serviceSessionRunning: (s) => s.serviceSession?.running === true,
  serviceCountdownRemaining: (s) => {
    const tick = s.serviceSession?.tick
    void tick
    const sess = s.serviceSession
    if (!sess) return 0
    const presets = s.layout?.global?.servicePresets || []
    const id = s.layout?.global?.activeServiceId
    const svc = presets.find((p) => p.id === id) || presets[0] || null
    const wallclock = sess.mode === 'wallclock' || svc?.tMinusMode === 'wallclock'
    if (wallclock) {
      if (!sess.running) {
        if (sess.pausedRemaining != null) return sess.pausedRemaining
        return remainingForPreset(svc)
      }
      const targetAt = sess.targetAt || resolveTargetAt(svc?.tMinusTargetTime)
      return Math.round((targetAt - Date.now()) / 1000)
    }
    if (!sess.running) {
      return sess.pausedRemaining != null ? sess.pausedRemaining : sess.duration
    }
    if (!sess.anchorMs) return sess.remainingAtStart
    return sess.remainingAtStart - Math.floor((Date.now() - sess.anchorMs) / 1000)
  },
}

const actions = {
  async fetchWorkspaces({ commit }) {
    const { data } = await missionApi.listWorkspaces()
    commit('SET_WORKSPACES', data.data.workspaces || [])
    return data.data.workspaces || []
  },

  async createWorkspace({ dispatch }, name) {
    const { data } = await missionApi.createWorkspace(name)
    await dispatch('fetchWorkspaces')
    return data.data.workspace
  },

  async deleteWorkspace({ dispatch, state }, id) {
    const wasActive = Number(state.activeWorkspaceId) === Number(id)
    await missionApi.deleteWorkspace(id)
    const list = await dispatch('fetchWorkspaces')
    if (wasActive && list.length) {
      return list[0].id
    }
    return null
  },

  async fetchLayout({ commit, state, dispatch }, workspaceId) {
    const id = workspaceId || state.activeWorkspaceId
    if (!id) return
    const { data } = await missionApi.getWorkspace(id)
    const ws = data.data.workspace
    commit('SET_ACTIVE_WORKSPACE', { id: ws.id, name: ws.name })
    commit('SET_LAYOUT', ws.layout)
    dispatch('initServiceSession')
  },

  async fetchCatalog({ commit }) {
    const { data } = await missionApi.getWidgetCatalog()
    commit('SET_CATALOG', data.data.widgets || [])
    commit('SET_PRESETS', data.data.presets || {})
  },

  async saveLayout({ state, commit }) {
    if (!state.activeWorkspaceId || !state.layout) return
    commit('SET_SAVING', true)
    try {
      await missionApi.saveWorkspace(state.activeWorkspaceId, {
        name: state.activeWorkspaceName,
        layout: state.layout,
      })
    } finally {
      commit('SET_SAVING', false)
    }
  },

  async renameWorkspace({ state, commit, dispatch }, name) {
    const trimmed = String(name || '').trim()
    if (!trimmed || !state.activeWorkspaceId || !state.layout) return
    if (trimmed === state.activeWorkspaceName) return

    commit('SET_ACTIVE_WORKSPACE', { id: state.activeWorkspaceId, name: trimmed })
    const list = (state.workspaces || []).map((w) => (
      w.id === state.activeWorkspaceId ? { ...w, name: trimmed } : w
    ))
    commit('SET_WORKSPACES', list)
    await dispatch('saveLayout')
  },

  async resetLayout({ commit, state, dispatch }) {
    if (!state.activeWorkspaceId) return
    const { data } = await missionApi.resetWorkspace(state.activeWorkspaceId)
    commit('SET_LAYOUT', data.data.layout)
    dispatch('initServiceSession')
  },

  toggleEditMode({ commit, state }) {
    commit('SET_EDIT_MODE', !state.editMode)
  },

  setActiveLayoutTab({ commit, state, dispatch }, tabId) {
    if (!state.layout || state.layout.activeTabId === tabId) return
    commit('SET_LAYOUT', setActiveTab(state.layout, tabId))
    dispatch('debouncedSave')
  },

  addLayoutTab({ commit, state, dispatch }, name) {
    if (!state.layout) return
    commit('SET_LAYOUT', addTab(state.layout, name))
    dispatch('debouncedSave')
  },

  removeLayoutTab({ commit, state, dispatch }, tabId) {
    if (!state.layout) return
    commit('SET_LAYOUT', removeTab(state.layout, tabId))
    dispatch('debouncedSave')
  },

  renameLayoutTab({ commit, state, dispatch }, { id, name }) {
    if (!state.layout || !id) return
    commit('SET_LAYOUT', renameTab(state.layout, id, name))
    dispatch('debouncedSave')
  },

  updateLayoutFromGrid({ commit, state, dispatch }, gridLayout) {
    let changed = false
    const widgets = (state.layout?.widgets || []).map((w) => {
      const item = gridLayout.find((g) => g.i === w.id)
      if (!item) return w
      const old = w.layout || {}
      if (old.x === item.x && old.y === item.y && old.w === item.w && old.h === item.h) return w
      changed = true
      return {
        ...w,
        layout: {
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h,
          minW: item.minW,
          minH: item.minH,
        },
      }
    })
    if (!changed) return
    commit('SET_LAYOUT', patchActiveWidgets(state.layout, widgets))
    dispatch('debouncedSave')
  },

  debouncedSave({ dispatch }) {
    if (saveTimer) clearTimeout(saveTimer)
    saveTimer = setTimeout(() => {
      saveTimer = null
      dispatch('saveLayout')
    }, 1000)
  },

  async flushSave({ dispatch }) {
    if (saveTimer) {
      clearTimeout(saveTimer)
      saveTimer = null
      await dispatch('saveLayout')
    }
  },

  addWidget({ commit, state, dispatch }, { type, config, x, y }) {
    const id = `w-${type}-${Date.now()}`
    const catalogItem = state.catalog.find((c) => c.type === type) || { defaultSize: { w: 3, h: 2 } }
    const minSize = WIDGET_REGISTRY[type]?.minSize || { w: 2, h: 2 }
    const size = catalogItem.defaultSize || { w: 3, h: 2 }
    const w = Math.max(size.w || 3, minSize.w || 2)
    const h = Math.max(size.h || 2, minSize.h || 2)
    const existing = state.layout?.widgets || []
    const colNum = state.layout?.gridCols || 12
    const place = findWidgetPlacement(existing, colNum, w, h, x, y)
    const widgets = [...existing, {
      id,
      type,
      config: config || {},
      layout: {
        x: place.x,
        y: place.y,
        w,
        h,
        minW: minSize.w || 2,
        minH: minSize.h || 2,
      },
    }]
    commit('SET_LAYOUT', patchActiveWidgets(state.layout, widgets))
    dispatch('debouncedSave')
  },

  removeWidget({ commit, state, dispatch }, widgetId) {
    const widgets = (state.layout?.widgets || []).filter((w) => w.id !== widgetId)
    commit('SET_LAYOUT', patchActiveWidgets(state.layout, widgets))
    dispatch('debouncedSave')
  },

  async updateWidgetConfig({ commit, state, dispatch }, { widgetId, config }) {
    const widgets = (state.layout?.widgets || []).map((w) => {
      if (w.id !== widgetId) return w
      return { ...w, config: { ...(w.config || {}), ...config } }
    })
    commit('SET_LAYOUT', patchActiveWidgets(state.layout, widgets))
    await dispatch('saveLayout')
  },

  applyPreset({ commit, dispatch, getters, state }, presetKey) {
    const preset = getters.presets[presetKey]
    if (!preset || !state.layout) return
    const theme = state.layout.theme === 'dark' ? 'dark' : 'light'
    const widgets = Array.isArray(preset.widgets) ? preset.widgets : []
    commit('SET_LAYOUT', patchActiveWidgets({
      ...state.layout,
      theme,
      gridCols: preset.gridCols || state.layout.gridCols,
      rowHeight: preset.rowHeight || state.layout.rowHeight,
    }, widgets))
    dispatch('debouncedSave')
  },

  setTheme({ commit, state, dispatch }, theme) {
    const next = theme === 'dark' ? 'dark' : 'light'
    if (!state.layout || state.layout.theme === next) return
    commit('SET_LAYOUT', { ...state.layout, theme: next })
    dispatch('debouncedSave')
  },

  async importLayout({ commit, dispatch, state }, raw) {
    if (!state.activeWorkspaceId) throw new Error('No active workspace')
    const { layout } = parseLayoutImport(raw)
    commit('SET_LAYOUT', layout)
    dispatch('initServiceSession')
    await dispatch('saveLayout')
  },

  initServiceSession({ commit, getters }) {
    commit('SET_SERVICE_SESSION', sessionPatchForPreset(getters.activeService))
  },

  activateService({ commit, state, dispatch }, serviceId) {
    const global = normalizeGlobal(state.layout?.global || {})
    const svc = global.servicePresets.find((p) => p.id === serviceId)
    if (!svc) return
    commit('SET_LAYOUT', { ...state.layout, global: { ...global, activeServiceId: serviceId } })
    commit('SET_SERVICE_SESSION', sessionPatchForPreset(svc))
    if (svc.autoStart) dispatch('startServiceCountdown')
    dispatch('debouncedSave')
  },

  startServiceCountdown({ commit, state, getters, dispatch }) {
    if (state.serviceSession.running) return
    const svc = getters.activeService
    const wallclock = svc?.tMinusMode === 'wallclock'
    const remaining = getters.serviceCountdownRemaining
    const cues = svc?.alertCues || []
    const targetAt = wallclock
      ? (state.serviceSession.targetAt || resolveTargetAt(svc.tMinusTargetTime))
      : null
    commit('SET_SERVICE_SESSION', {
      running: true,
      anchorMs: Date.now(),
      remainingAtStart: remaining,
      pausedRemaining: null,
      targetAt,
      mode: wallclock ? 'wallclock' : 'duration',
      alertCuesFired: pastCountdownCues(remaining, cues),
    })
    dispatch('maybeSendCountdownAlerts')
  },

  pauseServiceCountdown({ commit, state, getters }) {
    if (!state.serviceSession.running) return
    commit('SET_SERVICE_SESSION', {
      running: false,
      anchorMs: null,
      pausedRemaining: getters.serviceCountdownRemaining,
    })
  },

  resetServiceCountdown({ commit, getters }) {
    commit('SET_SERVICE_SESSION', sessionPatchForPreset(getters.activeService))
  },

  toggleServiceCountdown({ dispatch, getters }) {
    if (getters.serviceSessionRunning) dispatch('pauseServiceCountdown')
    else dispatch('startServiceCountdown')
  },

  tickServiceCountdown({ commit, state, getters, dispatch }) {
    const wallclock = getters.activeService?.tMinusMode === 'wallclock' || state.serviceSession?.mode === 'wallclock'
    if (!state.serviceSession.running && !wallclock) return
    commit('SERVICE_TICK')
    if (state.serviceSession.running) dispatch('maybeSendCountdownAlerts')
  },

  maybeSendCountdownAlerts({ commit, state, getters, dispatch }) {
    if (!state.serviceSession?.running) return
    const remaining = getters.serviceCountdownRemaining
    const cues = getters.activeService?.alertCues || []
    const fired = state.serviceSession.alertCuesFired || []
    const due = cues.filter((c) => remaining <= c.at && !fired.includes(c.at))
    if (!due.length) return
    const cue = due.reduce((a, b) => (a.at < b.at ? a : b))
    const style = cueAlertStyle(cue.at)
    commit('SET_SERVICE_SESSION', {
      alertCuesFired: [...fired, ...due.map((c) => c.at)],
    })
    dispatch('mission/sendAlert', {
      message: cue.message,
      color: style.color,
      icon: style.icon,
      autoClose: getters.activeService?.alertAutoClose || 5,
    }, { root: true }).catch(() => {})
  },

  async updateServicePresets({ commit, state, dispatch, getters }, presets) {
    const global = normalizeGlobal({ ...state.layout.global, servicePresets: presets })
    commit('SET_LAYOUT', { ...state.layout, global })
    if (!getters.serviceSessionRunning) {
      commit('SET_SERVICE_SESSION', sessionPatchForPreset(getters.activeService))
    }
    await dispatch('saveLayout')
  },
}

const mutations = {
  SET_WORKSPACES(state, workspaces) {
    state.workspaces = workspaces
  },
  SET_ACTIVE_WORKSPACE(state, { id, name }) {
    state.activeWorkspaceId = id
    state.activeWorkspaceName = name
  },
  SET_LAYOUT(state, layout) {
    state.layout = normalizeLayout(layout)
  },
  SET_EDIT_MODE(state, val) {
    state.editMode = val
  },
  SET_CATALOG(state, catalog) {
    state.catalog = catalog
  },
  SET_PRESETS(state, presets) {
    state.presets = presets
  },
  SET_SAVING(state, val) {
    state.saving = val
  },
  SET_SERVICE_SESSION(state, patch) {
    state.serviceSession = { ...state.serviceSession, ...patch }
  },
  SERVICE_TICK(state) {
    state.serviceSession = {
      ...state.serviceSession,
      tick: (state.serviceSession.tick || 0) + 1,
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
